import Icon from '@chakra-ui/icon';
import { Box, HStack, SimpleGrid, Text } from '@chakra-ui/layout';
import { ButtonGroup, IconButton } from '@chakra-ui/react';
import { RiAddLine, RiGroupLine } from 'react-icons/ri';
import { useAuth } from '../../../../../../../../../../../../../../../hooks/auth';
import {
  IWeekdayActivityScheduleBase,
  WaveSideEnum,
} from '../../../../../../../../../../../../../../../models/activities';
import { IWeekdayActivitySchedule } from '../../../../../../../../../../../../../../../services/Activities/ListWeekdaysActivitySchedulesService';
import {
  getScheduleAvailableVacancies,
  getScheduleCapacityView,
} from '../../../../../../../../../../../../../../../utils/activity-schedule-capacity';

interface IScheduleCardProps {
  schedules: IWeekdayActivityScheduleBase[];
  onCreateBooking: (schedule: IWeekdayActivitySchedule) => void;
  onCreateBookingQueue: (schedule: IWeekdayActivitySchedule) => void;
}

export const ScheduleCard = ({
  schedules,
  onCreateBooking,
  onCreateBookingQueue,
}: IScheduleCardProps): JSX.Element => {
  const { assignment: authenticatedAssignment, experienceLevelsTitleMap } =
    useAuth();
  const hasBookingQueueWriteAccess =
    authenticatedAssignment?.featureGroup.features.some((feature) =>
      ['BOOKING_QUEUE_FULL_ACCESS', 'BOOKING_QUEUE_WRITE_ACCESS'].includes(
        feature.key,
      ),
    ) ?? false;

  return (
    <SimpleGrid columns={3} spacing="2" w="100%">
      {schedules.map((schedule) => {
        const capacityView = getScheduleCapacityView(schedule);
        const isLodgerOnlyCapacity = capacityView.mode === 'LODGER_ONLY';
        const availableVacancies = getScheduleAvailableVacancies(schedule) ?? 0;

        const guestAvailableVacancies =
          typeof schedule.guestVacancies === 'number'
            ? schedule.guestVacancies - schedule.guestBooked
            : null;

        const lodgerAvailableVacancies =
          typeof schedule.lodgerVacancies === 'number'
            ? schedule.lodgerVacancies - schedule.lodgerBooked
            : null;

        const queueAvailableVacancies =
          schedule.queueVacancies - schedule.queueBooked;

        return (
          <Box
            key={schedule.activityScheduleId}
            borderWidth={1}
            borderRadius="8"
            p="2"
          >
            <HStack justify="space-between">
              <Text as="strong">
                {experienceLevelsTitleMap.get(schedule.experience)}
              </Text>

              {!schedule.isExpired && (
                <ButtonGroup>
                  <IconButton
                    aria-label="create-booking"
                    size="xs"
                    icon={<Icon as={RiAddLine} fontSize="18" />}
                    fontSize="16"
                    colorScheme="green"
                    onClick={() => onCreateBooking(schedule)}
                  />

                  {hasBookingQueueWriteAccess && (
                    <IconButton
                      aria-label="create-booking-queue"
                      size="xs"
                      icon={<Icon as={RiGroupLine} fontSize="18" />}
                      fontSize="16"
                      colorScheme="green"
                      onClick={() => onCreateBookingQueue(schedule)}
                    />
                  )}
                </ButtonGroup>
              )}
            </HStack>

            <Box>
              {schedule.modalityTitle && (
                <p>
                  <Text as="span">Modalidade: </Text>
                  <Text as="span">{schedule.modalityTitle}</Text>
                </p>
              )}

              {!!schedule.waveSide && (
                <p>
                  <Text as="span">Onda: </Text>
                  <Text as="span">{WaveSideEnum[schedule.waveSide]}</Text>
                </p>
              )}

              {isLodgerOnlyCapacity ? (
                <HStack spacing="1" fontSize="sm">
                  <Text>{capacityView.available ?? 'Ilimitado'}</Text>

                  <Text>vagas hóspedes</Text>
                </HStack>
              ) : (
                <>
                  {authenticatedAssignment?.featureGroup.features.some(
                    (feature) =>
                      feature.key === 'NON_LODGER_BOOKINGS_READ_ACCESS',
                  ) && <Text>{`${schedule.booked} reservas`}</Text>}

                  {authenticatedAssignment?.featureGroup.features.some(
                    (feature) =>
                      feature.key === 'NON_LODGER_VACANCIES_READ_ACCESS',
                  ) && (
                    <>
                      <HStack spacing="1" fontSize="sm">
                        <Text>
                          {availableVacancies >= 0 ? availableVacancies : 0}
                        </Text>

                        <Text>vagas disponíveis</Text>

                        {availableVacancies < 0 && (
                          <Text color="gray.500">
                            {`${availableVacancies * -1} reservado após limite`}
                          </Text>
                        )}
                      </HStack>

                      <HStack spacing="1" fontSize="sm">
                        <Text>
                          {availableVacancies > 0 &&
                          queueAvailableVacancies >= 0
                            ? queueAvailableVacancies
                            : 0}
                        </Text>

                        <Text>vagas disp. fila</Text>

                        {queueAvailableVacancies < 0 && (
                          <Text color="gray.500">
                            {`${
                              queueAvailableVacancies * -1
                            } reservado após limite`}
                          </Text>
                        )}
                      </HStack>

                      <HStack spacing="1" fontSize="sm">
                        {typeof guestAvailableVacancies === 'number' ? (
                          <Text>
                            {availableVacancies > 0 &&
                            guestAvailableVacancies >= 0
                              ? guestAvailableVacancies
                              : 0}
                          </Text>
                        ) : (
                          <Text>Ilimitado</Text>
                        )}

                        <Text>vagas disp. convidados</Text>

                        {typeof guestAvailableVacancies === 'number' &&
                          guestAvailableVacancies < 0 && (
                            <Text color="gray.500">
                              {`${
                                guestAvailableVacancies * -1
                              } reservado após limite`}
                            </Text>
                          )}
                      </HStack>
                    </>
                  )}

                  <HStack spacing="1" fontSize="sm">
                    {authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        feature.key === 'LODGER_VACANCIES_FULL_ACCESS',
                    ) && (
                      <>
                        {typeof lodgerAvailableVacancies === 'number' ? (
                          <Text>
                            {availableVacancies > 0 &&
                            lodgerAvailableVacancies >= 0
                              ? lodgerAvailableVacancies
                              : 0}
                          </Text>
                        ) : (
                          <Text>Ilimitado</Text>
                        )}

                        <Text>vagas disp. hóspedes</Text>

                        {schedule.isLodgerVacanciesExclusive && (
                          <Text color="gray.500">(exclusivas)</Text>
                        )}

                        {typeof lodgerAvailableVacancies === 'number' &&
                          lodgerAvailableVacancies < 0 && (
                            <Text color="gray.500">
                              {`${
                                lodgerAvailableVacancies * -1
                              } reservado após limite`}
                            </Text>
                          )}
                      </>
                    )}
                  </HStack>
                </>
              )}
            </Box>
          </Box>
        );
      })}
    </SimpleGrid>
  );
};
